/** Shared live-tracking map utilities (Table Top branded). */

export const BRAND = "#EE2677";
export const BURGUNDY = "#4A1028";

/** Driver GPS can jitter — require more movement before re-fetching directions. */
export const ROUTE_REFRESH_MIN_METERS_DRIVER = 220;
export const ROUTE_REFRESH_MIN_METERS_CUSTOMER = 140;
export const ROUTE_REFRESH_MAX_MS = 60000;
/** Legacy alias — customer default. */
export const ROUTE_REFRESH_MIN_METERS = ROUTE_REFRESH_MIN_METERS_CUSTOMER;

export const TABLETOP_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#FFF7FA" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6B4E57" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFBF5" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#EBB3A9" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#FFF0F6" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FFE8F0" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#EE2677", lightness: 40 }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#C9E4F5" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#7BA3C4" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#F5F2EA" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#EBB3A9", lightness: 20 }] },
];

export const CAKE_MARKER_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">' +
    '<circle cx="22" cy="22" r="20" fill="#FFFFFF" stroke="#EE2677" stroke-width="3"/>' +
    '<path fill="#EE2677" d="M14 26h16v3H14zm2-8c0-2 1.5-3.5 3.5-3.5S23 16 23 18h-2c0-1-.8-1.5-1.5-1.5S18 17 18 18h-2zm6 0c0-2 1.5-3.5 3.5-3.5S31 16 31 18h-2c0-1-.8-1.5-1.5-1.5S26 17 26 18h-2z"/>' +
    '<rect x="14" y="20" width="16" height="6" rx="2" fill="#F9A8D4"/>' +
    '<rect x="14" y="18" width="16" height="3" rx="1" fill="#EE2677"/>' +
    "</svg>"
);

export const HOME_MARKER_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
    '<circle cx="20" cy="20" r="18" fill="#FFFFFF" stroke="#EE2677" stroke-width="2.5"/>' +
    '<path fill="#EE2677" d="M20 10 11 18h2.5v11h5.5v-6.5h2v6.5H26.5V18H29z"/>' +
    "</svg>"
);

let directionsService = null;

function getDirectionsService() {
  if (!window.google?.maps?.DirectionsService) return null;
  if (!directionsService) {
    directionsService = new window.google.maps.DirectionsService();
  }
  return directionsService;
}

export function waitForGoogleMaps(maxMs = 12000) {
  return new Promise((resolve) => {
    const ready = () =>
      Boolean(window.google?.maps?.Map && window.google?.maps?.LatLngBounds);
    if (ready()) {
      resolve(true);
      return;
    }
    const start = Date.now();
    const tick = () => {
      if (ready()) {
        resolve(true);
        return;
      }
      if (Date.now() - start > maxMs) {
        resolve(false);
        return;
      }
      setTimeout(tick, 200);
    };
    tick();
  });
}

export function telHref(tel) {
  const t = (tel || "").trim();
  return t ? `tel:${t}` : null;
}

export function waHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

export function distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const g = window.google?.maps;
  if (g?.geometry?.spherical) {
    return g.geometry.spherical.computeDistanceBetween(
      new g.LatLng(a.lat, a.lng),
      new g.LatLng(b.lat, b.lng)
    );
  }
  const r = 6371000;
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
  return r * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function shouldRefreshRoute(
  lastOrigin,
  newOrigin,
  lastFetchMs,
  nowMs = Date.now(),
  minMeters = ROUTE_REFRESH_MIN_METERS_CUSTOMER
) {
  if (!lastOrigin || !newOrigin) return true;
  const age = nowMs - (lastFetchMs || 0);
  if (age >= ROUTE_REFRESH_MAX_MS) return true;
  return distanceMeters(lastOrigin, newOrigin) >= minMeters;
}

export function pathDistanceMeters(path) {
  if (!path || path.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    total += distanceMeters(path[i - 1], path[i]);
  }
  return total;
}

/** ~22 km/h average in urban delivery traffic. */
export function estimateMinutesFromMeters(meters, metersPerMinute = 370) {
  if (!meters || meters <= 0) return null;
  return Math.max(1, Math.round(meters / metersPerMinute));
}

export function nearestPathIndex(path, pos) {
  if (!path?.length || !pos) return 0;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < path.length; i += 1) {
    const d = distanceMeters(path[i], pos);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Keep route line ahead of the driver — avoids full polyline redraws between API calls. */
export function trimRouteFromPosition(path, pos) {
  if (!path?.length || !pos) return path || [];
  if (path.length < 2) return [pos, ...path];
  const idx = nearestPathIndex(path, pos);
  const ahead = path.slice(idx);
  if (ahead.length < 2) return [pos, path[path.length - 1]];
  return [pos, ...ahead.slice(1)];
}

export function fitMapToRoute(map, path, padding = {}) {
  if (!map || !path?.length || !window.google?.maps) return;
  const bounds = new window.google.maps.LatLngBounds();
  path.forEach((p) => bounds.extend(p));
  map.fitBounds(bounds, {
    top: padding.top ?? 56,
    right: padding.right ?? 36,
    bottom: padding.bottom ?? 140,
    left: padding.left ?? 36,
  });
}

/** Tight Uber-style framing: driver + drop-off only, with zoom clamps. */
export function fitMapToDriverAndDest(
  map,
  driver,
  dest,
  padding = {},
  { minZoom = 14, maxZoom = 17 } = {}
) {
  if (!map || !driver || !dest || !window.google?.maps) return;
  const bounds = new window.google.maps.LatLngBounds();
  bounds.extend(driver);
  bounds.extend(dest);
  map.fitBounds(bounds, {
    top: padding.top ?? 48,
    right: padding.right ?? 32,
    bottom: padding.bottom ?? 48,
    left: padding.left ?? 32,
  });
  window.google.maps.event.addListenerOnce(map, "idle", () => {
    const z = map.getZoom();
    if (z == null) return;
    if (z < minZoom) map.setZoom(minZoom);
    else if (z > maxZoom) map.setZoom(maxZoom);
  });
}

/**
 * Street-level zoom for DA — fixed levels so roads are readable on phone.
 * Centers between rider and home; fullscreen gets +1 zoom when close.
 */
export function driverStreetZoom(distMeters, fullscreen = false) {
  let z = 13;
  if (distMeters < 200) z = 18;
  else if (distMeters < 450) z = 17;
  else if (distMeters < 900) z = 16;
  else if (distMeters < 1800) z = 15;
  else if (distMeters < 4000) z = 14;
  if (fullscreen && distMeters < 3000) z = Math.min(18, z + 1);
  return z;
}

/** Weighted center — bias toward rider so the road ahead is visible. */
export function driverMapCenter(driver, dest) {
  if (!driver) return dest;
  if (!dest) return driver;
  const dist = distanceMeters(driver, dest);
  const bias = dist < 600 ? 0.62 : dist < 1500 ? 0.52 : 0.45;
  return {
    lat: dest.lat + (driver.lat - dest.lat) * bias,
    lng: dest.lng + (driver.lng - dest.lng) * bias,
  };
}

/** DA driver camera — navigation-style fixed zoom (not wide fitBounds). */
export function applyDriverMapCamera(map, path, driver, dest, { fullscreen = false } = {}) {
  if (!map || !dest || !window.google?.maps) return;

  const dist = driver ? distanceMeters(driver, dest) : Infinity;
  const zoom = driverStreetZoom(dist, fullscreen);
  const center = driverMapCenter(driver, dest);

  map.setZoom(zoom);
  map.panTo(center);

  // When very close, nudge once more after tiles load so road labels render crisply.
  if (driver && dist < 1200) {
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      const want = driverStreetZoom(dist, fullscreen);
      if (z != null && want > z) map.setZoom(want);
      map.panTo(center);
    });
  }
}

/**
 * @deprecated use applyDriverMapCamera — kept for customer path fitting
 */
export function fitMapToDriverRouteView(map, path, driver, dest, padding = {}, opts = {}) {
  applyDriverMapCamera(map, path, driver, dest, opts);
}

export function clampMapZoom(map, minZoom, maxZoom) {
  if (!map) return;
  const z = map.getZoom();
  if (z == null) return;
  if (z < minZoom) map.setZoom(minZoom);
  else if (z > maxZoom) map.setZoom(maxZoom);
}

/** Google driving route (roads), not a straight geodesic line. */
export function fetchDrivingRoute(origin, destination) {
  return new Promise((resolve) => {
    const fallback = () =>
      resolve({
        path: origin && destination ? [origin, destination] : [],
        durationMinutes: null,
      });

    const svc = getDirectionsService();
    if (!svc || !origin || !destination) {
      fallback();
      return;
    }

    const g = window.google.maps;
    svc.route(
      {
        origin: new g.LatLng(origin.lat, origin.lng),
        destination: new g.LatLng(destination.lat, destination.lng),
        travelMode: g.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result, status) => {
        if (status !== window.google.maps.DirectionsStatus.OK || !result?.routes?.[0]) {
          fallback();
          return;
        }
        const route = result.routes[0];
        const path = (route.overview_path || []).map((p) => ({ lat: p.lat(), lng: p.lng() }));
        const leg = route.legs?.[0];
        const durationMinutes =
          leg?.duration?.value != null
            ? Math.max(1, Math.round(leg.duration.value / 60))
            : null;
        resolve({
          path: path.length ? path : [origin, destination],
          durationMinutes,
        });
      }
    );
  });
}

/** Smooth marker glide between GPS polls. */
export function animateMarkerTo(marker, target, durationMs = 1400) {
  if (!marker || !target) return;
  const start = marker.getPosition?.();
  if (!start) {
    marker.setPosition(target);
    return;
  }
  const startLat = start.lat();
  const startLng = start.lng();
  const dLat = target.lat - startLat;
  const dLng = target.lng - startLng;
  if (Math.abs(dLat) < 1e-7 && Math.abs(dLng) < 1e-7) return;

  const t0 = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - t0) / durationMs);
    const ease = t * (2 - t);
    marker.setPosition({
      lat: startLat + dLat * ease,
      lng: startLng + dLng * ease,
    });
    if (t < 1) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}

let panAnimFrame = null;

/** Smooth camera follow — keeps current zoom (Uber-style). */
export function smoothPanTo(map, target, durationMs = 900) {
  if (!map || !target) return;
  const center = map.getCenter?.();
  if (!center) {
    map.panTo(target);
    return;
  }
  const startLat = center.lat();
  const startLng = center.lng();
  const dLat = target.lat - startLat;
  const dLng = target.lng - startLng;
  if (Math.abs(dLat) < 1e-7 && Math.abs(dLng) < 1e-7) return;

  if (panAnimFrame) window.cancelAnimationFrame(panAnimFrame);
  const t0 = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - t0) / durationMs);
    const ease = t * (2 - t);
    map.panTo({
      lat: startLat + dLat * ease,
      lng: startLng + dLng * ease,
    });
    if (t < 1) {
      panAnimFrame = window.requestAnimationFrame(step);
    } else {
      panAnimFrame = null;
    }
  };
  panAnimFrame = window.requestAnimationFrame(step);
}

export function mapFitPadding(fullscreen, variant = "customer") {
  const isDriver = variant === "driver";
  return {
    top: fullscreen ? 72 : isDriver ? 22 : 48,
    bottom: fullscreen ? 200 : isDriver ? 26 : 130,
    left: isDriver ? 18 : 28,
    right: isDriver ? 18 : 28,
  };
}

/** True when drop-off pin + fresh driver GPS are both live (safe to show ETA). */
export function trackingEtaReady(
  tracking,
  { livePosition = null, locationSharing = false, variant = "customer" } = {}
) {
  if (!tracking) return false;
  const hasDest =
    tracking.destination_lat != null && tracking.destination_lng != null;
  if (!hasDest) return false;

  if (tracking.eta_ready === false) return false;
  if (tracking.eta_ready === true) {
    if (variant === "driver") {
      return Boolean(
        livePosition?.lat != null &&
          livePosition?.lng != null &&
          locationSharing
      );
    }
    return true;
  }

  if (variant === "driver") {
    return Boolean(
      livePosition?.lat != null &&
        livePosition?.lng != null &&
        locationSharing &&
        !tracking.stale
    );
  }

  return Boolean(
    tracking.has_driver_location &&
      !tracking.stale &&
      tracking.driver_lat != null &&
      tracking.driver_lng != null
  );
}

export function trackingEtaBufferMessage(
  tracking,
  { livePosition = null, locationSharing = false, variant = "customer" } = {}
) {
  const hasDest =
    tracking?.destination_lat != null && tracking?.destination_lng != null;

  if (!hasDest) {
    return variant === "driver" ? "Pinning drop-off on map…" : "Setting drop-off pin…";
  }

  if (variant === "driver") {
    if (livePosition?.lat == null) return "Allow location — ETA when GPS is on";
    if (!locationSharing) return "Buffering — syncing your location…";
    if (tracking?.stale) return "Buffering — refreshing GPS…";
    return "Buffering — matching route to customer…";
  }

  if (!tracking?.has_driver_location) {
    return "Buffering — waiting for driver GPS…";
  }
  if (tracking?.stale) {
    return "Buffering — refreshing driver location…";
  }
  return "Buffering — syncing live route…";
}

export function resolveTrackingEta(
  tracking,
  routeEtaMinutes,
  { livePosition = null, locationSharing = false, variant = "customer" } = {}
) {
  const ready = trackingEtaReady(tracking, { livePosition, locationSharing, variant });
  const minutes = ready
    ? routeEtaMinutes ?? tracking?.eta_minutes ?? null
    : null;
  return {
    ready,
    minutes,
    bufferMessage: ready
      ? null
      : trackingEtaBufferMessage(tracking, { livePosition, locationSharing, variant }),
  };
}
